import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { apiFetch } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { getAllowedScreenData } from "./screenScopes";

function formatNameWithId(name, id) {
  return `${name} (ID: ${id})`;
}

function Printers() {
  const { auth, hasRole } = useAuth();
  const canEditScreens = hasRole("screens:edit");
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sublocations, setSublocations] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    macAddress: "",
    locationId: "",
    sublocationId: "",
    targetLabel: "",
  });

  const { allowedSites, allowedLocations, allowedSublocations } = useMemo(
    () =>
      getAllowedScreenData({
        roles: auth.roles || [],
        sites,
        locations,
        sublocations,
      }),
    [auth.roles, sites, locations, sublocations]
  );

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const [sitesRes, locationsRes, sublocationsRes, printersRes] = await Promise.all([
        apiFetch("/updates-reports/sites"),
        apiFetch("/updates-reports/locations"),
        apiFetch("/updates-reports/sublocations"),
        apiFetch("/settings/printers"),
      ]);

      const [sitesData, locationsData, sublocationsData, printersData] = await Promise.all([
        sitesRes.json(),
        locationsRes.json(),
        sublocationsRes.json(),
        printersRes.json(),
      ]);

      if (!sitesRes.ok) throw new Error(sitesData?.error || "Failed to load sites");
      if (!locationsRes.ok) throw new Error(locationsData?.error || "Failed to load locations");
      if (!sublocationsRes.ok) throw new Error(sublocationsData?.error || "Failed to load sublocations");
      if (!printersRes.ok) throw new Error(printersData?.error || "Failed to load printers");

      setSites(Array.isArray(sitesData) ? sitesData : []);
      setLocations(Array.isArray(locationsData) ? locationsData : []);
      setSublocations(Array.isArray(sublocationsData) ? sublocationsData : []);
      setPrinters(Array.isArray(printersData) ? printersData : []);
    } catch (err) {
      setError(err.message || "Failed to load printers");
      setSites([]);
      setLocations([]);
      setSublocations([]);
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  }

  function openAddPrinterDialog({ locationId, sublocationId = null, targetLabel }) {
    if (!canEditScreens) return;
    setFormData({
      name: "",
      macAddress: "",
      locationId: String(locationId),
      sublocationId: sublocationId == null ? "" : String(sublocationId),
      targetLabel,
    });
    setDialogOpen(true);
    setError("");
  }

  async function handleAddPrinter() {
    const trimmedName = formData.name.trim();
    const trimmedMacAddress = formData.macAddress.trim();

    if (!trimmedName || !trimmedMacAddress) {
      setError("Name and MAC Address are required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const res = await apiFetch("/settings/printers", {
        method: "POST",
        body: JSON.stringify({
          name: trimmedName,
          macAddress: trimmedMacAddress,
          locationId: Number(formData.locationId),
          sublocationId: formData.sublocationId ? Number(formData.sublocationId) : null,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to add printer");
      }

      setPrinters((current) => [...current, data]);
      setDialogOpen(false);
    } catch (err) {
      setError(err.message || "Failed to add printer");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeletePrinter(printerId) {
    setError("");

    try {
      const res = await apiFetch(`/settings/printers/${printerId}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to delete printer");
      }

      setPrinters((current) => current.filter((printer) => printer.id !== printerId));
    } catch (err) {
      setError(err.message || "Failed to delete printer");
    }
  }

  const sortedSites = [...allowedSites].sort((a, b) => Number(a.id) - Number(b.id));

  const getLocationPrinters = (locationId) =>
    printers
      .filter(
        (printer) => Number(printer.locationId) === Number(locationId) && (printer.sublocationId == null || printer.sublocationId === "")
      )
      .sort((a, b) => a.name.localeCompare(b.name));

  const getSublocationPrinters = (sublocationId) =>
    printers
      .filter((printer) => Number(printer.sublocationId) === Number(sublocationId))
      .sort((a, b) => a.name.localeCompare(b.name));

  const renderPrinterList = (printerList) => {
    if (!printerList.length) {
      return <Typography color="text.secondary">No printers yet.</Typography>;
    }

    return (
      <Stack spacing={1.5}>
        {printerList.map((printer) => (
          <Card key={printer.id} elevation={1}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box>
                  <Typography variant="h6">{printer.name}</Typography>
                  <Typography color="text.secondary">{printer.macAddress}</Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleDeletePrinter(printer.id)}
                  disabled={!canEditScreens}
                >
                  Delete
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Printers
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Card elevation={1}>
          <CardContent>
            <Typography color="text.secondary">Loading printer hierarchy...</Typography>
          </CardContent>
        </Card>
      ) : null}

      {!loading &&
        sortedSites.map((site) => {
          const siteLocations = allowedLocations
            .filter((location) => Number(location.site_id) === Number(site.id))
            .sort((a, b) => Number(a.id) - Number(b.id));

          if (!siteLocations.length) return null;

          return (
            <Box key={site.id} sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Site: {formatNameWithId(site.name, site.id)}
              </Typography>

              <Stack spacing={2}>
                {siteLocations.map((location) => {
                  const locationPrinters = getLocationPrinters(location.id);
                  const locationSublocations = allowedSublocations
                    .filter((sublocation) => Number(sublocation.location_id) === Number(location.id))
                    .sort((a, b) => Number(a.id) - Number(b.id));

                  return (
                    <Card key={location.id} elevation={2}>
                      <CardContent>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          spacing={2}
                          sx={{ mb: 2 }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Location: {formatNameWithId(location.name, location.id)}
                            </Typography>
                            <Typography color="text.secondary">
                              Location-level printers can be shared across sublocations.
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            disabled={!canEditScreens}
                            onClick={() =>
                              openAddPrinterDialog({
                                locationId: location.id,
                                targetLabel: `Location: ${location.name}`,
                              })
                            }
                          >
                            Add Printer
                          </Button>
                        </Stack>

                        {renderPrinterList(locationPrinters)}

                        {locationSublocations.map((sublocation) => (
                          <Box
                            key={sublocation.id}
                            sx={{
                              mt: 2,
                              ml: { xs: 0, md: 3 },
                              pl: { xs: 0, md: 2 },
                              borderLeft: { xs: "none", md: "3px solid #e5e7eb" },
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              justifyContent="space-between"
                              alignItems={{ xs: "flex-start", md: "center" }}
                              spacing={2}
                              sx={{ mb: 1.5 }}
                            >
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  Sublocation: {formatNameWithId(sublocation.name, sublocation.id)}
                                </Typography>
                                <Typography color="text.secondary">
                                  Sublocation-level printers stay local to this expo area.
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                disabled={!canEditScreens}
                                onClick={() =>
                                  openAddPrinterDialog({
                                    locationId: location.id,
                                    sublocationId: sublocation.id,
                                    targetLabel: `Sublocation: ${sublocation.name}`,
                                  })
                                }
                              >
                                Add Printer
                              </Button>
                            </Stack>

                            {renderPrinterList(getSublocationPrinters(sublocation.id))}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          );
        })}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Printer</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {formData.targetLabel}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(event) => setFormData((current) => ({ ...current, name: event.target.value }))}
              fullWidth
            />
            <TextField
              label="MAC Address"
              value={formData.macAddress}
              onChange={(event) => setFormData((current) => ({ ...current, macAddress: event.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPrinter} disabled={saving}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Printers;
